import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Colors,
  FontSizes,
  FontFamilies,
  Theme,
  isValidEmail,
  Image,
  Logger,
  isValidProviderResult
} from 'common';
import {
  Button,
  Checkbox,
  DateSelect,
  Text,
  TextInput,
  Select,
} from 'common/components';
import { HtmlModal } from 'ui/components';
import { withCommon } from 'common/hocs';
import { OrderProvider } from 'providers';

const Container = styled.div`
  padding: 20px;
  padding-bottom: 120px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MaxWidthContainer = styled.div`
  width: 100%;
  max-width: 650px;
`;

const Title = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Big};
  margin-bottom: 4px;
  margin-top: 20px;
  display: block;
`;

const Divide = styled.div`
  background-color: ${Theme.EventIdentity.DividerColor};
  height: 1px;
  width: 100%;
  margin: 15px 0;
`;

const CustomFieldContainer = styled.div`
  margin: 15px 0;
  width: 100%;
`;

const CustomFieldLabel = styled(Text)`
  display: block;
  padding: 0 0 5px 0;
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${Colors.Error};
    }
  `}
`;

const CustomFieldImageContainer = styled.div`
  margin: 20px 0 10px 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CustomFieldImage = styled(Image)`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 5px;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 12px 20px;
  gap: 20px;
  background-color: ${Theme.Content.Background || '#fff'};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const ButtonContainerInner = styled.div`
  width: 100%;
  max-width: 650px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;


const TermsLink = styled(Text)`
  color: ${Colors.PrimaryOne || '#0066cc'};
  font-family: ${FontFamilies.Bold};
  text-decoration: underline;
  cursor: pointer;
  display: inline;
  margin-left: 5px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const EditCustomFieldsComponent = (props) => {
  const locationState = props.common.location?.state || {};
  const orderId = locationState.orderId;
  const initialCustomFields = useMemo(() => locationState.customFields || [], [locationState.customFields]);

  const [fields, setFields] = useState(() => {
    // Initialize fields with existing values
    const initialFields = {};
    if (initialCustomFields && Array.isArray(initialCustomFields)) {
      initialCustomFields.forEach(field => {
        initialFields[field.id] = {
          ...field,
          value: field.value || ''
        };
      });
    }
    return initialFields;
  });

  const [showTermsModal, setShowTermsModal] = useState(false);

  // Update fields when customFields prop changes
  useEffect(() => {
    if (initialCustomFields && Array.isArray(initialCustomFields)) {
      const updatedFields = {};
      initialCustomFields.forEach(field => {
        updatedFields[field.id] = {
          ...field,
          value: field.value || ''
        };
      });
      setFields(updatedFields);
    }
  }, [initialCustomFields]);

  // Redirect if no orderId or customFields
  useEffect(() => {
    if (!orderId || !initialCustomFields || initialCustomFields.length === 0) {
      props.common.showToast('Invalid order data. Redirecting...');
      props.common.navigate('/profile/view-my-tickets');
    }
  }, [orderId, initialCustomFields]); // eslint-disable-line

  const validateCustomField = (field, value) => {
    if (field.required && (!value || (Array.isArray(value) && !value.length) || (typeof value === 'string' && !value.trim()))) {
      return `${field.label} is required`;
    }

    if (!value || (typeof value === 'string' && !value.trim())) return null; // Don't validate empty optional fields

    // temp fix for boland
    if (field.label?.startsWith('Smart Shopper number')) {
      let ssValue = typeof value === 'string' ? value.trim() : '';
      
      if (ssValue.length !== 16) {
        return 'Smart Shopper number must be 16 digits';
      }

      if ("7353" !== ssValue.substring(0, 4)) {
        return 'Smart Shopper number is invalid, please confirm the number entered is correct';
      }
    }

    switch (field.type) {
      case 'email':
        return isValidEmail(value) ? null : `${field.label} must be a valid email`;
      
      case 'tel':
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        return phoneRegex.test(value) ? null : `${field.label} must be a valid phone number`;
      
      case 'number':
        return !isNaN(value) ? null : `${field.label} must be a valid number`;

      case 'date': {
        const str = typeof value === 'string' ? value.trim() : '';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          return `${field.label} must be a valid date`;
        }
        const t = new Date(`${str}T12:00:00`).getTime();
        return !Number.isNaN(t) ? null : `${field.label} must be a valid date`;
      }
      
      default:
        return null;
    }
  };

  const validateAllFields = () => {
    if (!initialCustomFields || !Array.isArray(initialCustomFields) || initialCustomFields.length === 0) return true;

    const errors = initialCustomFields.reduce((acc, field) => {
      const error = validateCustomField(field, fields[field.id]?.value);
      if (error) acc.push(error);
      return acc;
    }, []);

    if (errors.length > 0) {
      props.common.showToast(errors.join('. '));
      return false;
    }

    return true;
  };

  const onFieldChange = (fieldId, value) => {
    setFields(prevFields => ({
      ...prevFields,
      [fieldId]: {
        ...prevFields[fieldId],
        value: value
      }
    }));
  };

  const renderCustomField = (field) => {
    const fieldData = fields[field.id] || field;
    const value = fieldData.value || '';
    const commonProps = {
      width: '100%',
      value: value,
      onChange: (value) => onFieldChange(field.id, value),
      placeholder: field.placeholder || `Enter ${field.label.toLowerCase()}...`,
    };
  
    switch (field.type) {
      case 'textarea':
        return (
          <TextInput
            {...commonProps}
            multiline={true}
          />
        );
  
      case 'number':
        return (
          <TextInput
            {...commonProps}
            type="number"
          />
        );
  
      case 'email':
        return (
          <TextInput
            {...commonProps}
            type="email"
          />
        );
  
      case 'tel':
        return (
          <TextInput
            {...commonProps}
            type="tel"
          />
        );

      case 'date':
        return (
          <DateSelect
            width={commonProps.width}
            value={commonProps.value}
            onChange={commonProps.onChange}
          />
        );
  
      case 'checkbox':
        return (
          <Checkbox
            checked={!!value}
            label={field.placeholder || ''}
            onClick={(checked) => onFieldChange(field.id, checked)}
          />
        );
  
      case 'select':
        return (
          <Select
            {...commonProps}
            options={field.options.map(opt => ({
              value: opt,
              label: opt
            }))}
            placeholder={`Select ${field.label.toLowerCase()}...`}
          />
        );
  
      default: // text
        return (
          <TextInput
            {...commonProps}
            type="text"
          />
        );
    }
  };

  const handleSave = async () => {
    if (!validateAllFields()) {
      return;
    }

    // Prepare the updated custom fields array
    const updatedCustomFields = initialCustomFields.map(field => {
      const updatedField = fields[field.id];
      if (updatedField) {
        return updatedField;
      }
      return field;
    });

    props.common.showLoading();

    try {
      const response = await OrderProvider.updateOrderForLoggedInUser(orderId, updatedCustomFields);

      if (!isValidProviderResult(response)) {
        throw new Error(response.message || 'Failed to update custom fields');
      }
      
      // Navigate back to view tickets page with update success flag
      props.common.history.push('/profile/view-my-tickets', { updateSuccess: true });
    } catch (error) {
      props.common.showToast('Oh no, something went wrong, please try again.');
      Logger.error('EditCustomFields', `handleSave (${orderId})`, error);
      props.common.hideLoading();
    }
  };

  const handleBack = () => {
    props.common.navigate('/profile/view-my-tickets');
  };

  if (!orderId || !initialCustomFields || initialCustomFields.length === 0) {
    return null;
  }

  return (
    <>
      <Container>
        <MaxWidthContainer>
          <Text
            maxWidth={'1100px'}
            margin={'0 auto'}
            width={'100%'}
            display={'block'} 
            padding={'5px 0 0 0'} 
            fontSize={FontSizes.Small}
            color={Theme.Text.Color}
            onClick={handleBack}>
              ← Back
          </Text>
          
          <Title>Manage Details</Title>
          
          <Text
            display={'block'}
            fontSize={FontSizes.Smaller}
            padding={'0'}>
            After you have updated your details, please click the "Save" button to save your changes.
          </Text>

          <Divide/>

          {initialCustomFields.map((field, index) => {
            // Check for image field - support multiple possible property names
            const imageUrl = field.image || field.image_url || field.imageUrl;
            
            return (
              <CustomFieldContainer key={`custom-field-${index}`}>
                {field.label?.startsWith('Smart Shopper number') && (
                  <CustomFieldImageContainer>
                    <CustomFieldImage
                      src={imageUrl || 'https://storage.googleapis.com/ticketing-files/events-products/pnp_smart.png'}
                      alt={field.label || 'Custom field image'}
                    />
                  </CustomFieldImageContainer>
                )}
                <CustomFieldLabel
                  fontSize={FontSizes.Smaller}
                  required={field.required}>
                  {field.label}
                </CustomFieldLabel>
                {field.description && (
                  <Text
                    fontSize={FontSizes.Smallest}
                    color={Colors.Grey}
                    display={'block'}
                    padding={'0 0 5px 0'}>
                    {field.description}
                    {field.label?.startsWith('Smart Shopper number') && (
                      <TermsLink
                        fontSize={FontSizes.Smaller}
                        onClick={() => setShowTermsModal(true)}>
                        Terms and Conditions
                      </TermsLink>
                    )}
                  </Text>
                )}
                {renderCustomField(field)}
              </CustomFieldContainer>
            );
          })}
        </MaxWidthContainer>
      </Container>

      <ButtonContainer>
        <ButtonContainerInner>
          <Button 
            onClick={handleBack} 
            backgroundColor={Theme.Button.Secondary.Background}
            width={'100%'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            width={'100%'}
          >
            Save
          </Button>
        </ButtonContainerInner>
      </ButtonContainer>

      <HtmlModal 
        url={'https://storage.googleapis.com/website777/weTicket/boland_rugby/legal/termsc_pickpayV2.html?v=' + (window.pnpTermsTimestamp || '1')} 
        show={showTermsModal} 
        onClose={() => setShowTermsModal(false)}
      />
    </>
  );
};

export const EditCustomFields = withCommon(
  EditCustomFieldsComponent,
  {
    showLoadingOnLoad: false
  }
);

